import React, { useState, useEffect } from 'react';
import {
  FaUserCircle, FaStar, FaEnvelope, FaSignOutAlt,
  FaTachometerAlt, FaProjectDiagram,
  FaHandshake, FaTools, FaUsers, FaChevronRight,
  FaPaperPlane, FaCommentDots, FaImage, FaBox, FaMinus, FaPlus, FaFileCsv, FaCheckCircle,
  FaCreditCard, FaCalendarAlt, FaMapMarkedAlt, FaMicrophone, FaQrcode, FaBell, FaMapMarkerAlt, FaLocationArrow, FaExclamationTriangle,
  FaMoneyBillWave, FaCalendarCheck, FaClock, FaHistory, FaBars, FaTimes,
  FaGlobe, FaMoon, FaSun, FaPalette
} from 'react-icons/fa';
import pb from '../lib/pocketbase';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import styles from './Account.module.css';
import { toast } from 'react-toastify';
import { formatLocalDate } from '../utils/timesheetHelpers';

// Tab Components
import TabOverview from '../components/account/TabOverview';
import TabProfile from '../components/account/TabProfile';
import TabProjects from '../components/account/TabProjects';
import TabPhotos from '../components/account/TabPhotos';
import TabMessages from '../components/account/TabMessages';
import TabPlanning from '../components/account/TabPlanning';
import TabWorkSites from '../components/account/TabWorkSites';
import TabMyHours from '../components/account/TabMyHours';
import TabLiveChantier from '../components/account/TabLiveChantier';
import TabInventory from '../components/account/TabInventory';
import TabTeamManagement from '../components/account/TabTeamManagement';
import TabTimesheetAdmin from '../components/account/TabTimesheetAdmin';
import TabRecruitment from '../components/account/TabRecruitment';
import TabExpenses from '../components/account/TabExpenses';
import TabBooking from '../components/account/TabBooking';

function AccountPage() {
  const navigate = useNavigate();
  const { openAuthModal, user } = useAuth();
  const { theme, toggleTheme, primaryColor, setPrimaryColor, themes } = useTheme();
  const [showThemeDropdown, setShowThemeDropdown] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const initIsStaff = ['employee', 'foreman', 'worker', 'bureau', 'artisan_terrain'].includes(user?.role);
  const [activeTab, setActiveTab] = useState(initIsStaff ? 'live' : 'overview');
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [projectPhotos, setProjectPhotos] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [inventory, setInventory] = useState([]);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const messageListRef = React.useRef(null);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [staffProjects, setStaffProjects] = useState([]);
  const [applications, setApplications] = useState([]);
  const [staffTimesheets, setStaffTimesheets] = useState([]);
  const [staffExpenses, setStaffExpenses] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [clientEvents, setClientEvents] = useState([]);
  const [loadingPlanning, setLoadingPlanning] = useState(false);
  const [showTimesheetModal, setShowTimesheetModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [sliderValue, setSliderValue] = useState(50);
  const [newTimesheet, setNewTimesheet] = useState({
    chantier: '',
    date: formatLocalDate(new Date()),
    hours: '',
    morning_start: '',
    morning_end: '',
    afternoon_start: '',
    afternoon_end: '',
    description: ''
  });
  const [checklist, setChecklist] = useState({
    clean: false,
    secure: false,
    photos: false
  });
  // Geolocation State
  const [userLocation, setUserLocation] = useState(null);
  const [locationStatus, setLocationStatus] = useState('idle'); // idle, locating, success, error

  // Phase 2 Artisan Reporting States
  const [reportPhase, setReportPhase] = useState('POUTRES_MURS');
  const [isCriticalPoint, setIsCriticalPoint] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [voiceNote, setVoiceNote] = useState('');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [todaySummary, setTodaySummary] = useState(null);

  const userRole = user?.role || 'client';
  const isEmployeeOrAdmin = ['employee', 'admin', 'foreman', 'worker', 'bureau', 'artisan_terrain'].includes(userRole);

  const [activeWorkSession, setActiveWorkSession] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [priorElapsedTime, setPriorElapsedTime] = useState(0);
  const [nearestProject, setNearestProject] = useState(null);
  const [bonuses, setBonuses] = useState({ lunchVouchers: 0, zoneBonus: 0 });

  // Scroll-to-top button visibility
  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (activeTab === 'overview') {
      fetchProjects();
      fetchMessages();
    }

    if (activeTab === 'projects') {
      fetchProjects();
    }
    if ((activeTab === 'live' || activeTab === 'worksites' || activeTab === 'hours' || activeTab === 'staff') && isEmployeeOrAdmin) {
      fetchStaffData();
    }
    if (activeTab === 'profile') {
      fetchProjects();
      fetchStaffData();
    }
    if (activeTab === 'messages') {
      fetchMessages();
      markMessagesAsRead();
    }
    if (activeTab === 'inventory') {
      fetchInventory();
    }
    if (activeTab === 'planning') {
      fetchClientPlanning();
    }
    if (activeTab === 'photos') {
      fetchProjectPhotos();
    }
    if (activeTab === 'recruitment' || (activeTab === 'overview' && userRole === 'admin')) {
      fetchApplications();
    }
  }, [activeTab, user?.role, user?.id]);

  useEffect(() => {
    if (showTimesheetModal && selectedProject) {
      setNewTimesheet(prev => ({ 
        ...prev, 
        chantier: selectedProject.id,
        morning_start: prev.morning_start || '08:00',
        morning_end: prev.morning_end || '12:00',
        afternoon_start: prev.afternoon_start || '13:00',
        afternoon_end: prev.afternoon_end || '16:00',
        hours: prev.hours || '7'
      }));
    }
  }, [showTimesheetModal, selectedProject]);

  // --- SMART DAILY REMINDERS ---
  useEffect(() => {
    if (!user?.id || !['worker', 'foreman'].includes(user.role)) return;

    const checkMissingHours = () => {
      const now = new Date();
      const hour = now.getHours();
      const day = now.getDay(); // 0: Sun, 1: Mon, ..., 5: Fri, 6: Sat

      // 1. Evening reminder (missing today's hours)
      if (hour >= 17) {
        const todayStr = formatLocalDate(now);
        const hasToday = staffTimesheets.some(ts => ts.date && ts.date.startsWith(todayStr));
        if (!hasToday && !activeWorkSession) {
          toast.warning("🔔 Journée terminée ? N'oubliez pas de déclarer vos heures dans 'Mes Heures' !", {
            toastId: 'missing-hours-today',
            position: 'top-center'
          });
        }
      }

      // 2. Friday/Saturday reminder (signature)
      if ((day === 5 && hour >= 16) || day === 6) {
        toast.info("📅 C'est la fin de semaine ! Pensez à vérifier et signer votre récapitulatif hebdo.", {
          toastId: 'signature-reminder',
          position: 'top-center'
        });
      }
    };

    const timer = setTimeout(checkMissingHours, 5000); // Check 5s after load
    return () => clearTimeout(timer);
  }, [user?.id, staffTimesheets, activeWorkSession]);

  // --- REAL-TIME ENGINE ---
  useEffect(() => {
    let isMounted = true;
    if (!user?.id) return;

    const initSubscriptions = async () => {
      // Small delay to allow previous connections to close cleanly
      await new Promise(resolve => setTimeout(resolve, 300));
      if (!isMounted || !user?.id) return;

      try {
        // 1. Messages (Ligne Directe)
        // We listen to all messages but filter locally for performance vs simplicity
        await pb.collection('messages').subscribe('*', (e) => {
          if (!isMounted) return;
          if (e.action === 'create') {
            // Only notify if recipient is current user
            if (e.record.recipient === user.id) {
              toast.info(`Nouveau message de ${e.record.sender_name || 'Maison Quinard'}`);
            }
            fetchMessages(); // Refresh list for everyone to handle general/admin chats
          }
        });


        // 4. Projects (Chantiers)
        await pb.collection('chantiers').subscribe('*', (e) => {
          if (!isMounted) return;
          fetchProjects();
          if (user.role === 'admin' || user.role === 'worker' || user.role === 'foreman') {
            fetchStaffData();
          }
        });

        // 5. Inventory (Stocks)
        await pb.collection('inventory').subscribe('*', (e) => {
          if (!isMounted) return;
          fetchInventory();
        });

        // 6. Interventions (Planning)
        await pb.collection('interventions').subscribe('*', (e) => {
          if (!isMounted) return;
          fetchClientPlanning();
        });

        // 7. Work Sessions (Chrono)
        await pb.collection('work_sessions').subscribe('*', (e) => {
          if (!isMounted) return;
          if (e.record.user === user.id) fetchActiveSession();
        });

      } catch (err) {
        if (err.status !== 0 && err.name !== 'ClientResponseError') {
          console.error("Real-time subscription error:", err);
        }
      }
    };

    initSubscriptions();

    return () => {
      isMounted = false;
      pb.collection('messages').unsubscribe('*').catch(() => { });
      pb.collection('chantiers').unsubscribe('*').catch(() => { });
      pb.collection('inventory').unsubscribe('*').catch(() => { });
      pb.collection('interventions').unsubscribe('*').catch(() => { });
      pb.collection('work_sessions').unsubscribe('*').catch(() => { });
      pb.collection('users').unsubscribe('*').catch(() => { });
    };
  }, [user?.id]);

  const fetchApplications = async () => {
    if (user?.role !== 'admin') return;
    try {
      const records = await pb.collection('users').getFullList({
        filter: 'profile_type = "candidate"',
        sort: '-created',
      });
      setApplications(records);
    } catch (error) {
      console.error('Error fetching applications:', error);
    }
  };

  const fetchClientPlanning = async () => {
    if (!user || !user.id) return;
    setLoadingPlanning(true);
    try {
      // 1. Get user's projects first
      const myProjects = await pb.collection('chantiers').getFullList({
        filter: `client = "${user.id}"`,
      });

      if (myProjects.length === 0) {
        setClientEvents([]);
        return;
      }

      // 2. Build a filter for interventions linked to these projects
      // user.id check might be redundant if we trust project association, but good for safety
      const projectIds = myProjects.map(p => `project="${p.id}"`).join(' || ');

      const records = await pb.collection('interventions').getFullList({
        filter: `(${projectIds})`,
        sort: 'start',
        expand: 'project',
      });

      // Transform for display
      const formattedEvents = records.map(record => ({
        id: record.id,
        date: new Date(record.start).getDate(),
        day: new Date(record.start).toLocaleDateString('fr-FR', { weekday: 'short' }),
        task: record.title,
        project: record.expand?.project?.name || 'Projet',
        status: record.status || 'pending', // confirmed, pending
        fullDate: new Date(record.start)
      }));

      setClientEvents(formattedEvents);
    } catch (error) {
      console.error('Error fetching planning:', error);
      // Fallback to empty or toast
    } finally {
      setLoadingPlanning(false);
    }
  };

  const fetchInventory = async () => {
    setLoadingInventory(true);
    try {
      const records = await pb.collection('inventory').getFullList({
        sort: 'name',
        expand: 'assigned_to',
      });
      setInventory(records);
    } catch (error) {
      console.error('Error fetching inventory:', error);
    } finally {
      setLoadingInventory(false);
    }
  };

  const handleUpdateStock = async (itemId, newQuantity) => {
    try {
      await pb.collection('inventory').update(itemId, {
        quantity: Math.max(0, newQuantity)
      });
      fetchInventory();
      toast.success("Stock mis à jour !");
    } catch (error) {
      console.error('Error updating stock:', error);
      toast.error("Erreur lors de la mise à jour du stock.");
    }
  };

  const handleAssignTool = async (itemId, userId = null) => {
    try {
      await pb.collection('inventory').update(itemId, {
        assigned_to: userId
      });
      toast.success(userId ? "Outil assigné !" : "Outil rendu !");
      fetchInventory();
    } catch (err) {
      console.error("Error assigning tool:", err);
      toast.error("Erreur lors de l'assignation.");
    }
  };

  // Auto-scroll to bottom of chat
  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [messages]);

  // Real-time Messages Subscription
  useEffect(() => {
    let isMounted = true;
    if (!user?.id) return;

    const initChatSubscription = async () => {
      await new Promise(resolve => setTimeout(resolve, 500));
      if (!isMounted || !user?.id) return;
      try {
        await pb.collection('messages').subscribe('*', async ({ action, record }) => {
          if (!isMounted) return;

          if (action === 'create') {
            try {
              const fullMessage = await pb.collection('messages').getOne(record.id, {
                expand: 'sender'
              });

              if (!isMounted) return;

              setMessages(prev => {
                // Avoid duplicates
                if (prev.some(m => m.id === record.id)) return prev;
                return [...prev, fullMessage];
              });

              if (record.sender !== user.id && activeTab !== 'messages') {
                setUnreadCount(prev => prev + 1);
              }
            } catch (err) {
              console.error("Error fetching new message details:", err);
            }
          }
        }, { expand: 'sender' });
      } catch (err) {
        if (err.status !== 0 && err.name !== 'ClientResponseError') {
          console.error("Account chat subscription error:", err);
        }
      }
    };

    initChatSubscription();

    return () => {
      isMounted = false;
      pb.collection('messages').unsubscribe('*').catch(() => { });
    };
  }, [user?.id, activeTab]);

  const markMessagesAsRead = async () => {
    setUnreadCount(0);
    // Here you would also update the backend if you track is_read per user
  };

  const fetchMessages = async () => {
    if (!user || !user.id) return;
    setLoadingMessages(true);
    try {
      const records = await pb.collection('messages').getFullList({
        sort: 'created',
        expand: 'sender',
      });
      setMessages(records);

      // Calculate unread (messages not sent by user and received after last logout or similar)
      // For simplicity, we'll reset on load if on the tab
      if (activeTab === 'messages') setUnreadCount(0);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      await pb.collection('messages').create({
        body: newMessage,
        content: newMessage, // Keep content for legacy if needed
        sender: user.id,
        // For now, messages go to admin/general
      });
      setNewMessage('');
      // No need to fetch, subscription handles it
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error("Erreur lors de l'envoi du message.");
    }
  };




  const fetchProjects = async () => {
    if (!user || !user.id) return;
    setLoadingProjects(true);
    try {
      const isAdminRole = ['admin', 'employee', 'bureau'].includes(user.role);
      const isStaff = ['worker', 'foreman'].includes(user.role);

      let filter = '';
      if (!isAdminRole) {
        if (isStaff) {
          filter = `chef_chantier ?~ "${user.id}" || workers ?~ "${user.id}"`;
        } else {
          filter = `client = "${user.id}"`;
        }
      }

      const records = await pb.collection('chantiers').getFullList({
        sort: '-created',
        filter,
      });
      setProjects(records);
    } catch (error) {
      console.error('Error fetching projects:', error);
      // Silently handle to avoid annoying toasts on load
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchProjectPhotos = async () => {
    if (!user || !user.id) return;
    setLoadingPhotos(true);
    try {
      // Find active chantiers for this client
      const myChantiers = await pb.collection('chantiers').getFullList({
        filter: user.role === 'client' ? `client = "${user.id}"` : '',
      });

      if (myChantiers.length === 0 && user.role === 'client') {
        setProjectPhotos([]);
        return;
      }

      const chantierIds = myChantiers.map(c => `chantier = "${c.id}"`).join(' || ');
      const filter = user.role === 'client' ? `(${chantierIds})` : '';

      const records = await pb.collection('chantiers_photos').getFullList({
        filter,
        sort: '-created',
        expand: 'chantier,worker',
      });

      const photosWithUrls = records.map(photo => ({
        ...photo,
        url: pb.files.getURL(photo, photo.file),
        thumb: pb.files.getURL(photo, photo.file, { thumb: '400x300' })
      }));

      setProjectPhotos(photosWithUrls);
    } catch (error) {
      console.error('Error fetching project photos:', error);
      // If collection doesn't exist yet, we'll just show empty
      setProjectPhotos([]);
    } finally {
      setLoadingPhotos(false);
    }
  };



  // Calculate cumulative prior elapsed time for today
  useEffect(() => {
    if (!user?.id) return;
    const fetchDailyPastTime = async () => {
      try {
        const midnightLocal = new Date();
        midnightLocal.setHours(0, 0, 0, 0);
        const midnightUTC = midnightLocal.toISOString().replace('T', ' ').substring(0, 19);
        const pastSessions = await pb.collection('work_sessions').getFullList({
          filter: `user="${user.id}" && created >= "${midnightUTC}" && is_active=false`
        });

        let pastSeconds = 0;
        pastSessions.forEach(s => {
          if (s.type === 'repas') return; // Do not count lunch break in work time
          const m1 = new Date(s.start_time).getTime();
          const m2 = new Date(s.updated).getTime();
          if (m2 > m1) {
            pastSeconds += Math.floor((m2 - m1) / 1000);
          }
        });
        setPriorElapsedTime(pastSeconds);
      } catch (err) { }
    };
    fetchDailyPastTime();
  }, [user?.id, activeWorkSession]);

  // Chrono logic in AccountPage
  useEffect(() => {
    let interval;
    if (activeWorkSession && activeWorkSession.type !== 'repas') {
      const start = new Date(activeWorkSession.start_time).getTime();
      interval = setInterval(() => {
        const now = new Date().getTime();
        setElapsedTime(priorElapsedTime + Math.floor((now - start) / 1000));
      }, 1000);
    } else {
      setElapsedTime(priorElapsedTime); // Pause chrono during 'repas' or if no session
    }
    return () => clearInterval(interval);
  }, [activeWorkSession, priorElapsedTime]);

  // 0. Initial Active Session Fetch
  useEffect(() => {
    if (user?.id && isEmployeeOrAdmin) {
      fetchActiveSession();
    }
  }, [user?.id, isEmployeeOrAdmin]);

  const fetchActiveSession = async () => {
    try {
      const active = await pb.collection('work_sessions').getFirstListItem(`user="${user.id}" && is_active=true`, {
        expand: 'chantier'
      });
      setActiveWorkSession(active);
    } catch (err) {
      setActiveWorkSession(null);
    }
  };

  const handleStartSession = async (projectId) => {
    if (!projectId) return;
    try {
      const session = await pb.collection('work_sessions').create({
        user: user.id,
        chantier: projectId,
        start_time: new Date().toISOString(),
        is_active: true
      });
      setActiveWorkSession(session);
      toast.success("Bonne journée ! Chrono démarré.");
    } catch (err) {
      toast.error("Erreur lors du démarrage du chrono.");
    }
  };

  const handleStopSession = async () => {
    if (!activeWorkSession) return;
    try {
      const startTime = new Date(activeWorkSession.start_time);
      const endTime = new Date();
      const diffMs = endTime - startTime;
      const hours = Math.round((diffMs / (1000 * 60 * 60)) * 10) / 10;

      // Extract location if available
      let gpsSnippet = "";
      if (userLocation) {
        gpsSnippet = ` (GPS: ${userLocation.lat.toFixed(4)}, ${userLocation.lng.toFixed(4)})`;
      }

      // Update session
      await pb.collection('work_sessions').update(activeWorkSession.id, {
        is_active: false
      });

      // Create Timesheet
      await pb.collection('timesheets').create({
        worker: user.id,
        chantier: activeWorkSession.chantier,
        date: activeWorkSession.start_time,
        hours: Math.max(0.5, hours),
        description: "Session chronométrée" + gpsSnippet,
        status: 'pending_manager'
      });

      setActiveWorkSession(null);
      fetchStaffData();
      toast.success(`Chrono arrêté. ${hours}h ajoutées !`);
    } catch (err) {
      toast.error("Erreur lors de l'arrêt du chrono.");
    }
  };

  // 1. Auto-find nearest project via GPS
  useEffect(() => {
    if (activeTab === 'hours' && (userRole === 'worker' || userRole === 'foreman')) {
      findNearestProject();
    }
  }, [activeTab]);

  // Calculate distance between two points (Haversine formula)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const findNearestProject = () => {
    if (!navigator.geolocation) return;

    setLocationStatus('locating');
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude: lat, longitude: lon } = pos.coords;
      setUserLocation({ lat, lng: lon });
      setLocationStatus('success');

      // Sort projects by distance
      const projectsWithDistance = staffProjects
        .filter(p => (p.status === 'EN_COURS' || p.status === 'SITE') && p.lat && p.lon)
        .map(p => ({
          ...p,
          distance: calculateDistance(lat, lon, parseFloat(p.lat), parseFloat(p.lon))
        }))
        .sort((a, b) => a.distance - b.distance);

      if (projectsWithDistance.length > 0) {
        const nearest = projectsWithDistance[0];
        setNearestProject(nearest);

        // SMART GEOFENCE ALERT: Suggest clock-in if < 0.5km
        if (nearest.distance < 0.5 && !activeWorkSession) {
          toast.info(`📍 Arrivée détectée sur le chantier ${nearest.name}. Démarrer le chrono ?`, {
            onClick: () => handleStartSession(nearest.id),
            autoClose: 10000,
            position: "bottom-center"
          });
        }
      }
    }, (err) => {
      setLocationStatus('error');
    });
  };

  const handleQuickValid7h = async () => {
    if (!nearestProject) {
      toast.error("Chantier non détecté.");
      return;
    }

    try {
      setLoadingStaff(true);
      await pb.collection('timesheets').create({
        worker: user.id,
        chantier: nearestProject.id,
        date: new Date().toISOString(),
        hours: 7,
        description: "Saisie Rapide (Auto)",
        status: 'pending_manager'
      });
      toast.success(`7h validées sur ${nearestProject.name}`);
      fetchStaffData();
    } catch (err) {
      toast.error("Erreur lors de la validation.");
    } finally {
      setLoadingStaff(false);
    }
  };

  const calculateBonuses = (timesheets) => {
    if (!Array.isArray(timesheets)) return { lunchVouchers: 0, zoneBonus: 0, overtime: 0 };

    const getCleanGroups = (tsList) => {
      const dayGroups = {};
      tsList.forEach(ts => {
        const dKey = ts.date?.substring(0, 10);
        if (!dKey) return;
        if (!dayGroups[dKey]) {
          dayGroups[dKey] = { mS: ts.morning_start, mE: ts.morning_end, aS: ts.afternoon_start, aE: ts.afternoon_end, h: ts.hours || 0 };
        } else {
          const g = dayGroups[dKey];
          if (ts.morning_start && (!g.mS || ts.morning_start < g.mS)) g.mS = ts.morning_start;
          if (ts.morning_end && (!g.mE || ts.morning_end > g.mE)) g.mE = ts.morning_end;
          if (ts.afternoon_start && (!g.aS || ts.afternoon_start < g.aS)) g.aS = ts.afternoon_start;
          if (ts.afternoon_end && (!g.aE || ts.afternoon_end > g.aE)) g.aE = ts.afternoon_end;
        }
      });
      return Object.values(dayGroups).map(day => {
        const h = (s, e) => {
          if (!s || !e || s === "--:--" || e === "" || s === "") return 0;
          const [hS, mS] = s.split(':').map(Number);
          const [hE, mE] = e.split(':').map(Number);
          return Math.max(0, (hE + mE / 60) - (hS + mS / 60));
        };
        const total = h(day.mS, day.mE) + h(day.aS, day.aE);
        return { total: total > 0 ? total : day.h };
      });
    };

    const now = new Date();
    const startOfWeek = new Date(now);
    const day = now.getDay();
    const diffToMonday = now.getDate() - day + (day === 0 ? -6 : 1);
    startOfWeek.setDate(diffToMonday);
    startOfWeek.setHours(0, 0, 0, 0);

    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    const monthlyTS = timesheets.filter(ts => ts.date && new Date(ts.date).getMonth() === currentMonth && new Date(ts.date).getFullYear() === currentYear);
    const weeklyTS = monthlyTS.filter(ts => new Date(ts.date) >= startOfWeek);

    const cleanMonthly = getCleanGroups(monthlyTS);
    const cleanWeekly = getCleanGroups(weeklyTS);

    const monthlyHours = cleanMonthly.reduce((acc, d) => acc + d.total, 0);
    const weeklyHours = cleanWeekly.reduce((acc, d) => acc + d.total, 0);
    const overtime = cleanMonthly.reduce((acc, d) => acc + Math.max(0, d.total - 7.8), 0);
    const lunchVouchers = cleanMonthly.filter(d => d.total > 2).length;

    return {
      lunchVouchers,
      overtime: parseFloat(overtime.toFixed(2)),
      weeklyHours: parseFloat(weeklyHours.toFixed(2)),
      monthlyHours: parseFloat(monthlyHours.toFixed(2))
    };
  };

  const fetchStaffData = async () => {
    setLoadingStaff(true);
    try {
      // Admin/Bureau/Employee see everything, others see only assigned
      const canSeeAll = ['admin', 'employee', 'bureau'].includes(user.role);
      const filter = !canSeeAll
        ? `chef_chantier ?~ "${user.id}" || workers ?~ "${user.id}" || status="EN_COURS" || status="PLANNING" || status="SITE"`
        : '';

      const projectRecords = await pb.collection('chantiers').getFullList({
        sort: '-created',
        filter
      });
      const realProjects = projectRecords.filter(p => p.description !== 'Projet interne généré automatiquement');
      setStaffProjects(realProjects);

      // 2. Fetch user's timesheets
      const timesheetRecords = await pb.collection('timesheets').getFullList({
        filter: !canSeeAll ? `worker = '${user.id}'` : '',
        sort: '-date',
        expand: 'chantier',
      });
      setStaffTimesheets(timesheetRecords);

      // 3. Fetch user's planning (interventions)
      const planningRecords = await pb.collection('interventions').getFullList({
        filter: !canSeeAll ? `workers ?~ '${user.id}'` : '',
        sort: '-start',
        expand: 'project'
      });
      setClientEvents(planningRecords);

      // 4. Fetch user's expenses
      const expenseRecords = await pb.collection('expenses').getFullList({
        filter: !canSeeAll ? `user = '${user.id}'` : '',
        sort: '-date',
      });
      setStaffExpenses(expenseRecords);
    } catch (error) {
      console.error('Error fetching staff data:', error);
      // Silently handle to avoid annoying toasts on load
    } finally {
      setLoadingStaff(false);
    }
  };


  const hasNew = (items) => {
    if (!items) return false;
    const fortyEightHoursAgo = new Date(Date.now() - 48 * 60 * 60 * 1000);
    return items.some(item => new Date(item.created) > fortyEightHoursAgo);
  };

  const getWeeklyStats = () => {
    const weeks = {};
    staffTimesheets.forEach(ts => {
      const date = new Date(ts.date);
      const day = date.getDay();
      const diff = date.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday
      const monday = new Date(date.setDate(diff));
      monday.setHours(0, 0, 0, 0);

      const weekKey = monday.toLocaleDateString();
      if (!weeks[weekKey]) {
        weeks[weekKey] = {
          label: `Semaine du ${monday.toLocaleDateString()}`,
          hours: 0,
          monday: monday
        };
      }
      weeks[weekKey].hours += ts.hours;
    });

    return Object.values(weeks).sort((a, b) => b.monday - a.monday);
  };




  const handleViewProject = (proj) => {
    setSelectedProject(proj);
    setShowProjectModal(true);
  };

  const exportWeeklyToCSV = () => {
    const stats = getWeeklyStats();
    if (stats.length === 0) {
      toast.info("Aucune donnée à exporter.");
      return;
    }

    const headers = ["Semaine", "Heures"];
    const rows = stats.map(s => [s.label, s.hours]);

    let csvContent = "data:text/csv;charset=utf-8,"
      + headers.join(",") + "\n"
      + rows.map(r => r.join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `recap_heures_${user.name.replace(/\s+/g, '_')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("CSV exporté avec succès !");
  };

  const exportWeeklyToPDF = () => {
    const stats = getWeeklyStats();
    if (stats.length === 0) {
      toast.info("Aucune donnée à exporter.");
      return;
    }

    try {
      const { jsPDF } = require("jspdf");
      require("jspdf-autotable");
      const doc = new jsPDF();

      // Header
      doc.setFontSize(22);
      doc.setTextColor(164, 198, 57); // #a4c639
      doc.text("Maison Quinard - Rapport d'Activité", 14, 20);

      doc.setFontSize(12);
      doc.setTextColor(100);
      doc.text(`Collaborateur : ${user.name || user.email}`, 14, 30);
      doc.text(`Généré le : ${new Date().toLocaleDateString('fr-FR')}`, 14, 37);

      // Weekly Table
      const tableColumn = ["Période", "Heures Cumulées", "Vérification"];
      const tableRows = stats.map(s => [s.label, `${s.hours}h`, "Validé"]);

      doc.autoTable({
        head: [tableColumn],
        body: tableRows,
        startY: 45,
        theme: 'striped',
        headStyles: { fillColor: [164, 198, 57] }
      });

      // Footer
      const finalY = doc.lastAutoTable.finalY || 150;
      doc.setFontSize(10);
      doc.text("Ceci est un document généré automatiquement par l'ERP Maison Quinard.", 14, finalY + 20);
      doc.text("Signature du responsable : __________________________", 14, finalY + 35);

      doc.save(`rapport_heures_${user.name.replace(/\s+/g, '_')}.pdf`);
      toast.success("PDF exporté avec succès !");
    } catch (err) {
      console.error("PDF export error:", err);
      toast.error("Veuillez installer jspdf pour activer l'export PDF.");
    }
  };



  const handlePhotoUpload = async (e) => {
    e.preventDefault();
    const files = e.target.querySelector('input[type="file"]').files;
    const caption = voiceNote || e.target.querySelector('textarea').value;

    if ((!files || files.length === 0) && !voiceNote) {
      toast.error("Veuillez ajouter une photo ou une note vocale.");
      return;
    }

    try {
      setLoadingPhotos(true);
      toast.info("Génération du rapport avec métadonnées techniques...");

      // If we have files, upload them
      if (files && files.length > 0) {
        for (let i = 0; i < files.length; i++) {
          const formData = new FormData();
          formData.append('chantier', selectedProject.id);
          formData.append('worker', user.id);
          formData.append('file', files[i]);

          // Enhanced caption with metadata
          let finalCaption = `[Phase: ${reportPhase.replace('_', ' ')}] `;
          if (isCriticalPoint) finalCaption += "⚠️ POINT CRITIQUE : ";
          finalCaption += caption || "Mise à jour chantier";

          formData.append('caption', finalCaption);
          const photoRecord = await pb.collection('chantiers_photos').create(formData);

          // Also create a site_activity record so it shows up in the Live Flux
          await pb.collection('site_activities').create({
            chantier: selectedProject.id,
            author: user.id,
            type: 'photo',
            content: `📸 Photo ajoutée : ${finalCaption}`,
            file: photoRecord.file // Link the file if needed, or just the reference
          });
        }
      } else if (voiceNote) {
        // If only a voice note, create a text-only update (optional logic)
        toast.info("Note technique enregistrée sans photo.");
      }

      toast.success("Rapport technique publié et notifié au client !");
      setShowUploadModal(false);
      setVoiceNote('');
      setIsCriticalPoint(false);
      fetchProjectPhotos();
      fetchProjects();
    } catch (error) {
      console.error('Error uploading project photos:', error);
      toast.error("Erreur lors de l'envoi du rapport.");
    } finally {
      setLoadingPhotos(false);
    }
  };

  // --- OFFLINE SYNC LOGIC ---
  const saveToOfflineQueue = (data) => {
    const queue = JSON.parse(localStorage.getItem('offline_timesheets') || '[]');
    queue.push({ ...data, id: Date.now(), pending: true });
    localStorage.setItem('offline_timesheets', JSON.stringify(queue));
    toast.info("📍 Mode hors-ligne : Saisie enregistrée localement. Elle sera synchronisée dès le retour du réseau.");
    setShowTimesheetModal(false);

    // Simulate updating local state for immediate feedback
    setStaffTimesheets(prev => [{
      ...data,
      id: 'local-' + Date.now(),
      expand: { chantier: staffProjects.find(p => p.id === data.chantier) || { name: 'Général' } }
    }, ...prev]);
  };

  const syncOfflineData = async () => {
    if (!navigator.onLine) return;
    const queue = JSON.parse(localStorage.getItem('offline_timesheets') || '[]');
    if (queue.length === 0) return;

    toast.info(`🔄 Synchronisation de ${queue.length} saisie(s) en cours...`);

    let successCount = 0;
    const newQueue = [];

    for (const item of queue) {
      try {
        await pb.collection('timesheets').create({
          worker: user.id,
          chantier: item.chantier === 'general' ? null : item.chantier,
          date: item.date,
          hours: parseFloat(item.hours),
          description: item.description,
          checklist_clean: item.checklist?.clean || false,
          checklist_secure: item.checklist?.secure || false,
          checklist_photos: item.checklist?.photos || false,
        });
        successCount++;
      } catch (err) {
        console.error("Sync error for item:", item, err);
        newQueue.push(item);
      }
    }

    localStorage.setItem('offline_timesheets', JSON.stringify(newQueue));
    if (successCount > 0) {
      toast.success(`✅ ${successCount} saisie(s) synchronisée(s) !`);
      fetchStaffData();
    }
  };

  useEffect(() => {
    window.addEventListener('online', syncOfflineData);
    // Try to sync on mount if online
    if (navigator.onLine) syncOfflineData();
    return () => window.removeEventListener('online', syncOfflineData);
  }, []);

  // Geolocation Helper
  const verifyLocation = () => {
    setLocationStatus('locating');
    if (!navigator.geolocation) {
      setLocationStatus('error');
      // toast.error("Géolocalisation non supportée.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ latitude, longitude });
        setLocationStatus('success');
      },
      (error) => {
        console.error("Geolocation error:", error);
        setLocationStatus('error');
      }
    );
  };

  // Trigger location check and summary when modal opens
  useEffect(() => {
    if (showTimesheetModal) {
      verifyLocation();
      fetchTodaySummary();
    } else {
      setUserLocation(null);
      setLocationStatus('idle');
      setTodaySummary(null);
    }
  }, [showTimesheetModal]);

  const fetchTodaySummary = async () => {
    if (!user?.id) return;
    try {
      const midnightLocal = new Date();
      midnightLocal.setHours(0, 0, 0, 0);
      const midnightUTC = midnightLocal.toISOString().replace('T', ' ').substring(0, 19);
      const sessions = await pb.collection('work_sessions').getFullList({
        filter: `user="${user.id}" && created >= "${midnightUTC}"`,
        expand: 'chantier',
        sort: 'created'
      });

      if (sessions.length > 0) {
        let totalHours = 0;
        const recap = sessions.map(s => {
          const start = new Date(s.start_time);
          const end = s.is_active ? new Date() : new Date(s.updated);
          const diff = Math.max(0, (end - start) / (1000 * 60 * 60));
          totalHours += diff;

          let name = 'Activité';
          if (s.expand?.chantier) name = s.expand.chantier.name;
          else if (s.type === 'trajet') name = '🚚 Trajet';
          else if (s.type === 'depot') name = '🏭 Atelier';
          else if (s.type === 'magasin') name = '🛒 Achats';
          else if (s.type === 'bureau') name = '💼 Bureau';

          return {
            name,
            hours: diff,
            projectId: s.chantier
          };
        });

        // Group by mission
        const grouped = recap.reduce((acc, curr) => {
          const existing = acc.find(item => item.name === curr.name);
          if (existing) {
            existing.hours += curr.hours;
          } else {
            acc.push({ ...curr });
          }
          return acc;
        }, []);

        // Detect morning/afternoon splits
        let mStart = '', mEnd = '', aStart = '', aEnd = '';

        const formatHHMM = (d) => {
          const hh = String(d.getHours()).padStart(2, '0');
          const mm = String(d.getMinutes()).padStart(2, '0');
          return `${hh}:${mm}`;
        };

        // Find the "repas" session
        const lunchSession = sessions.find(s => s.type === 'repas');

        if (lunchSession) {
          const morningSessions = sessions.filter(s => new Date(s.start_time) < new Date(lunchSession.start_time));
          const afternoonSessions = sessions.filter(s => new Date(s.start_time) > new Date(lunchSession.start_time));

          if (morningSessions.length > 0) {
            mStart = formatHHMM(new Date(morningSessions[0].start_time));
            mEnd = formatHHMM(new Date(lunchSession.start_time));
          }
          if (afternoonSessions.length > 0) {
            aStart = formatHHMM(lunchSession.is_active ? new Date() : new Date(lunchSession.updated));
            const lastSession = afternoonSessions[afternoonSessions.length - 1];
            const lastTime = lastSession.is_active ? new Date() : new Date(lastSession.updated);
            aEnd = formatHHMM(lastTime);
          }
        } else {
          // Fallback: use first and last session as morning
          if (sessions.length > 0) {
            mStart = formatHHMM(new Date(sessions[0].start_time));
            const last = sessions[sessions.length - 1];
            const lastEnd = last.is_active ? new Date() : new Date(last.updated);
            mEnd = formatHHMM(lastEnd);
          }
        }

        const finalTotal = Math.round(totalHours * 10) / 10;
        setTodaySummary({
          total: finalTotal,
          details: grouped,
          morning_start: mStart,
          morning_end: mEnd,
          afternoon_start: aStart,
          afternoon_end: aEnd
        });

        // Pre-fill
        const lastProjectSession = [...sessions].reverse().find(s => s.expand?.chantier);
        const internalTypes = ['atelier', 'depot', 'magasin', 'bureau', 'trajet', 'general'];
        let autoChantier = 'general';
        if (selectedProject) {
            autoChantier = internalTypes.includes(selectedProject.id) ? 'general' : selectedProject.id;
        } else if (lastProjectSession) {
            autoChantier = lastProjectSession.chantier;
        }

        setNewTimesheet(prev => ({
          ...prev,
          hours: finalTotal,
          chantier: autoChantier,
          morning_start: mStart,
          morning_end: mEnd,
          afternoon_start: aStart,
          afternoon_end: aEnd,
          description: grouped.map(g => `${g.name} (${Math.round(g.hours * 10) / 10}h)`).join(' + ')
        }));
      }
    } catch (err) {
      console.error("Summary error:", err);
    }
  };


  const handleTimesheetSubmit = async (e) => {
    e.preventDefault();

    // Append location to description
    let finalDescription = newTimesheet.description;
    if (userLocation) {
      finalDescription += `\n📍 GPS: ${userLocation.latitude.toFixed(4)}, ${userLocation.longitude.toFixed(4)}`;
    }

    const timesheetData = {
      worker: user.id,
      chantier: newTimesheet.chantier,
      date: newTimesheet.date,
      hours: newTimesheet.hours,
      morning_start: newTimesheet.morning_start,
      morning_end: newTimesheet.morning_end,
      afternoon_start: newTimesheet.afternoon_start,
      afternoon_end: newTimesheet.afternoon_end,
      description: finalDescription,
      checklist: checklist,
      status: 'pending_manager'
    };

    if (!navigator.onLine) {
      saveToOfflineQueue(timesheetData);
      return;
    }

    try {
      await pb.collection('timesheets').create({
        worker: user.id,
        chantier: newTimesheet.chantier === 'general' ? null : newTimesheet.chantier,
        date: newTimesheet.date,
        hours: parseFloat(newTimesheet.hours),
        morning_start: newTimesheet.morning_start,
        morning_end: newTimesheet.morning_end,
        afternoon_start: newTimesheet.afternoon_start,
        afternoon_end: newTimesheet.afternoon_end,
        description: finalDescription,
        checklist_clean: checklist.clean,
        checklist_secure: checklist.secure,
        checklist_photos: checklist.photos,
        status: 'pending_manager'
      });

      // Stop chrono and end session if active
      if (activeWorkSession) {
        await pb.collection('work_sessions').update(activeWorkSession.id, { is_active: false });
        if (activeWorkSession.chantier) {
          await pb.collection('site_activities').create({
            chantier: activeWorkSession.chantier,
            author: user.id,
            type: 'status',
            content: `✅ Fin de journée.`
          });
        }
        setActiveWorkSession(null);
        setElapsedTime(0);
      }

      toast.success("Heures déclarées avec succès !");
      setShowTimesheetModal(false);
      setNewTimesheet({
        chantier: '',
        date: formatLocalDate(new Date()),
        hours: '',
        description: ''
      });
      setChecklist({ clean: false, secure: false, photos: false });
      fetchStaffData();
    } catch (error) {
      console.error('Error submitting timesheet:', error);
      toast.error("Erreur lors de la déclaration.");
    }
  };

  // Form states (kept from previous version for functionality)
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!pb.authStore.isValid) {
      navigate('/');
      openAuthModal('login');
      return;
    }
  }, [navigate, openAuthModal]);

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };

  const handleLogout = () => {
    pb.authStore.clear();
    navigate('/');
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    try {
      await pb.collection('reviews').create({
        user: user.id,
        rating: parseInt(rating, 10),
        comment,
        status: 'pending',
      });
      showNotification('Avis envoyé avec succès !');
      setRating(5);
      setComment('');
    } catch (error) {
      showNotification("Erreur lors de l'envoi de l'avis.", 'error');
    }
  };

  const handleMessageSubmit = async (e) => {
    e.preventDefault();
    try {
      await pb.collection('messages').create({
        sender: user.id,
        subject,
        body: message,
        is_read: false,
      });
      showNotification('Message envoyé avec succès !');
      setSubject('');
      setMessage('');
    } catch (error) {
      showNotification("Erreur lors de l'envoi du message.", 'error');
    }
  };



  // Define initial tab based on role
  useEffect(() => {
    if (userRole === 'worker' || userRole === 'foreman' || userRole === 'artisan_terrain' || userRole === 'employee') {
      setActiveTab('live');
    } else if (userRole === 'client') {
      setActiveTab('overview');
    }
  }, [userRole]);

  const startVoiceInput = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error("La dictée vocale n'est pas supportée par votre navigateur.");
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = 'fr-FR';
    recognition.interimResults = false;

    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => setIsListening(false);

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setVoiceNote(prev => prev ? prev + ' ' + transcript : transcript);
      toast.success("Note dictée avec succès !");
    };

    recognition.start();
  };


  if (!pb.authStore.isValid || !user) {
    return null;
  }

  const menuItems = [
    { id: 'overview', label: "Vue d'ensemble", icon: <FaTachometerAlt />, roles: ['client', 'admin', 'user'] },
    { id: 'live', label: "EN DIRECT", icon: <FaBell />, roles: ['worker', 'foreman', 'admin', 'artisan_terrain'], showDot: true },
    { id: 'profile', label: "Mon Profil", icon: <FaUserCircle />, roles: ['client', 'foreman', 'worker', 'admin', 'user', 'artisan_terrain'] },
    { id: 'planning', label: "Mon Planning", icon: <FaCalendarAlt />, roles: ['foreman', 'worker', 'admin', 'artisan_terrain'] },
    { id: 'hours', label: "Mes Heures", icon: <FaClock />, roles: ['worker', 'foreman', 'admin', 'artisan_terrain'] },
    { id: 'worksites', label: "Mes Chantiers", icon: <FaTools />, roles: ['worker', 'foreman', 'admin', 'artisan_terrain'] },
    { id: 'photos', label: "Photos du Chantier", icon: <FaImage />, roles: ['client', 'foreman', 'worker', 'admin', 'artisan_terrain'] },
    { id: 'expenses', label: "Notes de Frais", icon: <FaMoneyBillWave />, roles: ['worker', 'foreman', 'admin', 'employee', 'bureau', 'artisan_terrain'] },

    // Autres items
    { id: 'projects', label: (userRole === 'user' || userRole === 'client') ? 'Mon Projet' : 'Mon Chantier', icon: <FaProjectDiagram />, roles: ['client', 'user'] },
    { id: 'messages', label: "Ligne Directe", icon: <FaCommentDots />, roles: ['client', 'admin', 'user', 'artisan_terrain'], showDot: unreadCount > 0, count: unreadCount },
    { id: 'booking', label: "Rendez-vous", icon: <FaCalendarCheck />, roles: ['client', 'user'] },
    { id: 'inventory', label: "Stocks & Matériel", icon: <FaBox />, roles: ['foreman', 'admin', 'artisan_terrain'] },
    { id: 'timesheet_admin', label: "Validation des Heures", icon: <FaClock />, roles: ['admin', 'employee', 'bureau'] },
    { id: 'users_admin', label: "Gestion d'Équipe", icon: <FaUsers />, roles: ['admin', 'bureau'] },
    { id: 'recruitment', label: "Candidatures", icon: <FaUsers />, roles: ['admin'] },
  ];

  const filteredMenu = menuItems.filter(item => item.roles.includes(userRole));

  const roleLabels = {
    'admin': 'Administrateur',
    'foreman': 'Chef de Chantier',
    'worker': 'Ouvrier',
    'employee': 'Collaborateur',
    'bureau': "Bureau d'études",
    'client': 'Client Privilégié'
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.dashboardContainer}>
        {notification.show && (
          <div className={`${styles.notification} ${styles[notification.type]}`}>
            {notification.message}
          </div>
        )}

        {/* --- MOBILE TOP NAV --- */}
        <div className={styles.mobileTopNav}>
          <div className={styles.mobileTopNavLeft}>
            <button onClick={() => setIsMobileMenuOpen(true)} className={styles.mobileTopNavMenuBtn}>
              <FaBars />
            </button>
            <span className={styles.mobileTopNavTitle}>Mon Espace</span>
          </div>
          <div className={styles.mobileTopNavRight}>
            <button onClick={toggleTheme} className={styles.mobileTopNavMenuBtn} title="Mode Jour/Nuit">
              {theme === 'light' ? '🌙' : '☀️'}
            </button>
          </div>
        </div>

        <div className={styles.dashboardLayout}>
          {/* SIDEBAR */}
          <aside className={styles.sidebar}>
            <h1 className={styles.sidebarTitle}>Maison Quinard</h1>
            <div className={styles.userRoleBadge}>{roleLabels[userRole] || 'Membre Maison Quinard'}</div>

            <nav className={styles.sidebarNav}>
              {/* GROUPE 1: GÉNÉRAL */}
              <div className={styles.navGroup}>
                <div className={styles.navSeparator}>Général</div>
                {filteredMenu.filter(item => ['overview', 'live', 'profile'].includes(item.id)).map(item => (
                  <button
                    key={item.id}
                    className={`${styles.navItem} ${activeTab === item.id ? styles.activeNav : ''}`}
                    onClick={() => setActiveTab(item.id)}
                  >
                    <span className={styles.navIcon}>
                      {item.icon}
                      {item.showDot && (
                        <span className={styles.notificationDot}>
                          {item.count > 0 ? item.count : ''}
                        </span>
                      )}
                    </span>
                    <span className={styles.navLabel}>{item.label}</span>
                    <FaChevronRight className={styles.navArrow} />
                  </button>
                ))}
              </div>

              {/* GROUPE 2: OPÉRATIONNEL */}
              {filteredMenu.some(item => ['planning', 'hours', 'worksites', 'inventory', 'timesheet_admin', 'users_admin', 'booking'].includes(item.id)) && (
                <div className={styles.navGroup}>
                  <div className={styles.navSeparator}>Opérationnel</div>
                  {filteredMenu.filter(item => ['planning', 'hours', 'worksites', 'inventory', 'timesheet_admin', 'users_admin', 'booking'].includes(item.id)).map(item => (
                    <button
                      key={item.id}
                      className={`${styles.navItem} ${activeTab === item.id ? styles.activeNav : ''}`}
                      onClick={() => setActiveTab(item.id)}
                    >
                      <span className={styles.navIcon}>
                        {item.icon}
                        {item.showDot && (
                          <span className={styles.notificationDot}>
                            {item.count > 0 ? item.count : ''}
                          </span>
                        )}
                      </span>
                      <span className={styles.navLabel}>{item.label}</span>
                      <FaChevronRight className={styles.navArrow} />
                    </button>
                  ))}
                </div>
              )}

              {/* GROUPE 3: COMMUNICATION & FICHIERS */}
              {filteredMenu.some(item => ['photos', 'expenses', 'projects', 'messages', 'recruitment'].includes(item.id)) && (
                <div className={styles.navGroup}>
                  <div className={styles.navSeparator}>Communication & Fichiers</div>
                  {filteredMenu.filter(item => ['photos', 'expenses', 'projects', 'messages', 'recruitment'].includes(item.id)).map(item => (
                    <button
                      key={item.id}
                      className={`${styles.navItem} ${activeTab === item.id ? styles.activeNav : ''}`}
                      onClick={() => setActiveTab(item.id)}
                    >
                      <span className={styles.navIcon}>
                        {item.icon}
                        {item.showDot && (
                          <span className={styles.notificationDot}>
                            {item.count > 0 ? item.count : ''}
                          </span>
                        )}
                      </span>
                      <span className={styles.navLabel}>{item.label}</span>
                      <FaChevronRight className={styles.navArrow} />
                    </button>
                  ))}
                </div>
              )}
            </nav>
            <div className={styles.sidebarFooter}>
              <Link to="/" className={styles.sidebarActionBtn} title="Retour au site public" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 15px', borderRadius: '8px', color: 'var(--color-text)', textDecoration: 'none', transition: 'background 0.2s' }}>
                <FaGlobe /> Retour au site
              </Link>
              <button onClick={toggleTheme} className={styles.sidebarActionBtn} title="Changer le thème" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 15px', borderRadius: '8px', color: 'var(--color-text)', background: 'transparent', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left', transition: 'background 0.2s' }}>
                {theme === 'light' ? <FaMoon /> : <FaSun />} {theme === 'light' ? 'Mode Sombre' : 'Mode Clair'}
              </button>
              
              <div style={{ height: '1px', background: 'var(--color-border, rgba(0,0,0,0.05))', margin: '10px 0' }}></div>
              
              <button className={styles.logoutBtn} onClick={handleLogout}>
                <FaSignOutAlt style={{ marginRight: '8px' }} /> Déconnexion
              </button>
            </div>
          </aside>

          {/* CONTENT AREA */}
          <main className={styles.mainContent}>
            {/* Header intégré au contenu (Proposition 1) */}
            <div className={styles.accountHeader}>
              <div className={styles.accountHeaderLeft}>
                <div className={styles.accountHeaderLogo}>MQ</div>
                <div>
                  <h1 className={styles.accountHeaderTitle}>Maison Quinard</h1>
                  <p className={styles.accountHeaderSub}>Portail Membres & Clients</p>
                </div>
              </div>
              <div className={styles.accountHeaderRight}>
                {/* Thème Couleur */}
                <div style={{ position: 'relative' }}>
                  <button onClick={() => setShowThemeDropdown(!showThemeDropdown)} className={styles.accountHeaderBtn} title="Personnaliser">
                    🎨
                  </button>
                  {showThemeDropdown && (
                    <div className={styles.accountThemeDropdown}>
                      <div className={styles.accountThemeDropdownTitle}>Thème couleur</div>
                      {Object.entries(themes).map(([key, t]) => (
                        <button
                          key={key}
                          className={`${styles.accountThemeOption} ${primaryColor === key ? styles.accountThemeOptionActive : ''}`}
                          style={{ borderColor: primaryColor === key ? t.primary : 'transparent' }}
                          onClick={() => { setPrimaryColor(key); setShowThemeDropdown(false); }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span>{t.icon}</span>
                            <span>{t.name}</span>
                          </div>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <span style={{ width: '14px', height: '14px', borderRadius: '50%', background: t.primary, display: 'block', border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }} />
                            <span style={{ width: '14px', height: '14px', borderRadius: '50%', background: t.accent, display: 'block', border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }} />
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Lune / Soleil */}
                <button onClick={toggleTheme} className={styles.accountHeaderBtn} title="Mode jour/nuit">
                  {theme === 'light' ? '🌙' : '☀️'}
                </button>

                {/* Retour site */}
                <Link to="/" className={styles.accountHeaderBackBtn}>
                  ← Site public
                </Link>
              </div>
            </div>

            {activeTab === 'overview' && (
              <TabOverview
                user={user}
                projects={projects}
                unreadCount={unreadCount}
                setActiveTab={setActiveTab}
                handleLogout={handleLogout}
                handleMessageSubmit={handleMessageSubmit}
                subject={subject}
                setSubject={setSubject}
                message={message}
                setMessage={setMessage}
                loading={loadingProjects}
                applications={applications}
                bonuses={calculateBonuses(staffTimesheets.filter(ts => ts.worker === user.id))}
                allTimeStats={(() => {
                  const mine = staffTimesheets.filter(ts => ts.worker === user.id);
                  const dayGroups = {};
                  mine.forEach(ts => {
                    const dKey = ts.date?.substring(0, 10);
                    if (!dKey) return;
                    if (!dayGroups[dKey]) {
                      dayGroups[dKey] = { mS: ts.morning_start, mE: ts.morning_end, aS: ts.afternoon_start, aE: ts.afternoon_end, h: ts.hours || 0 };
                    } else {
                      const g = dayGroups[dKey];
                      if (ts.morning_start && (!g.mS || ts.morning_start < g.mS)) g.mS = ts.morning_start;
                      if (ts.morning_end && (!g.mE || ts.morning_end > g.mE)) g.mE = ts.morning_end;
                      if (ts.afternoon_start && (!g.aS || ts.afternoon_start < g.aS)) g.aS = ts.afternoon_start;
                      if (ts.afternoon_end && (!g.aE || ts.afternoon_end > g.aE)) g.aE = ts.afternoon_end;
                    }
                  });
                  const cleanDays = Object.values(dayGroups).map(day => {
                    const hHelper = (s, e) => {
                      if (!s || !e || s === "--:--" || e === "" || s === "") return 0;
                      const [hS, mS] = s.split(':').map(Number);
                      const [hE, mE] = e.split(':').map(Number);
                      return Math.max(0, (hE + mE / 60) - (hS + mS / 60));
                    };
                    const tot = hHelper(day.mS, day.mE) + hHelper(day.aS, day.aE);
                    return tot > 0 ? tot : day.h;
                  });
                  return {
                    totalHours: cleanDays.reduce((acc, h) => acc + h, 0).toFixed(1),
                    totalOvertime: cleanDays.reduce((acc, h) => acc + Math.max(0, h - 7.8), 0).toFixed(1),
                    totalLunch: cleanDays.filter(h => h > 2).length
                  };
                })()}
              />
            )}

            {activeTab === 'profile' && (
              <TabProfile
                user={user}
                userRole={userRole}
                rating={rating}
                setRating={setRating}
                comment={comment}
                setComment={setComment}
                handleReviewSubmit={handleReviewSubmit}
                bonuses={calculateBonuses(staffTimesheets.filter(ts => ts.worker === user.id))}
                allTimeStats={(() => {
                  const mine = staffTimesheets.filter(ts => ts.worker === user.id);
                  const dayGroups = {};
                  mine.forEach(ts => {
                    const dKey = ts.date?.substring(0, 10);
                    if (!dKey) return;
                    if (!dayGroups[dKey]) {
                      dayGroups[dKey] = { mS: ts.morning_start, mE: ts.morning_end, aS: ts.afternoon_start, aE: ts.afternoon_end, h: ts.hours || 0 };
                    } else {
                      const g = dayGroups[dKey];
                      if (ts.morning_start && (!g.mS || ts.morning_start < g.mS)) g.mS = ts.morning_start;
                      if (ts.morning_end && (!g.mE || ts.morning_end > g.mE)) g.mE = ts.morning_end;
                      if (ts.afternoon_start && (!g.aS || ts.afternoon_start < g.aS)) g.aS = ts.afternoon_start;
                      if (ts.afternoon_end && (!g.aE || ts.afternoon_end > g.aE)) g.aE = ts.afternoon_end;
                    }
                  });
                  const cleanDays = Object.values(dayGroups).map(day => {
                    const hHelper = (s, e) => {
                      if (!s || !e || s === "--:--" || e === "" || s === "") return 0;
                      const [hS, mS] = s.split(':').map(Number);
                      const [hE, mE] = e.split(':').map(Number);
                      return Math.max(0, (hE + mE / 60) - (hS + mS / 60));
                    };
                    const tot = hHelper(day.mS, day.mE) + hHelper(day.aS, day.aE);
                    return tot > 0 ? tot : day.h;
                  });
                  return {
                    totalHours: cleanDays.reduce((acc, h) => acc + h, 0).toFixed(1),
                    totalOvertime: cleanDays.reduce((acc, h) => acc + Math.max(0, h - 7.8), 0).toFixed(1),
                    totalLunch: cleanDays.filter(h => h > 2).length
                  };
                })()}
                projects={projects}
                userExpenses={staffExpenses.filter(e => e.user === user.id)}
              />
            )}

            {activeTab === 'projects' && (
              <TabProjects
                loadingProjects={loadingProjects}
                projects={projects}
                fetchProjects={fetchProjects}
                handleViewProject={handleViewProject}
                setActiveTab={setActiveTab}
                userRole={userRole}
              />
            )}

            {activeTab === 'photos' && (
              <TabPhotos
                loadingPhotos={loadingPhotos}
                projectPhotos={projectPhotos}
                user={user}
              />
            )}





            {activeTab === 'planning' && (
              <TabPlanning
                loadingPlanning={loadingPlanning}
                clientEvents={clientEvents}
                setShowTimesheetModal={setShowTimesheetModal}
                setSelectedProject={setSelectedProject}
                staffProjects={staffProjects}
                staffTimesheets={staffTimesheets}
                user={user}
              />
            )}

            {activeTab === 'booking' && (
              <TabBooking user={user} />
            )}

            {isEmployeeOrAdmin && activeTab === 'worksites' && (
              <TabWorkSites
                loadingStaff={loadingStaff}
                staffProjects={staffProjects}
                setSelectedProject={setSelectedProject}
                setShowUploadModal={setShowUploadModal}
                fetchStaffData={fetchStaffData}
                checklist={checklist}
                setChecklist={setChecklist}
                user={user}
              />
            )}

            {isEmployeeOrAdmin && activeTab === 'hours' && (
              <TabMyHours
                staffTimesheets={staffTimesheets.filter(ts => ts.worker === user.id)}
                getWeeklyStats={getWeeklyStats}
                exportWeeklyToCSV={exportWeeklyToCSV}
                exportWeeklyToPDF={exportWeeklyToPDF}
                setShowTimesheetModal={setShowTimesheetModal}
                setSelectedProject={setSelectedProject}
                staffProjects={staffProjects}
                loadingStaff={loadingStaff}
                fetchStaffData={fetchStaffData}
                // New Artisan Features
                activeWorkSession={activeWorkSession}
                elapsedTime={elapsedTime}
                handleStartSession={handleStartSession}
                handleStopSession={handleStopSession}
                nearestProject={nearestProject}
                handleQuickValid7h={handleQuickValid7h}
                locationStatus={locationStatus}
                bonuses={calculateBonuses(staffTimesheets.filter(ts => ts.worker === user.id))}
                todaySummary={todaySummary}
                pb={pb}
                user={user}
                todayPlanning={clientEvents}
                setActiveTab={setActiveTab}
              />
            )}

            {isEmployeeOrAdmin && activeTab === 'live' && (
              <TabLiveChantier
                staffProjects={staffProjects}
                setSelectedProject={setSelectedProject}
                setShowTimesheetModal={setShowTimesheetModal}
                setShowUploadModal={setShowUploadModal}
                checklist={checklist}
                setChecklist={setChecklist}
                user={user}
                activeWorkSession={activeWorkSession}
                elapsedTime={elapsedTime}
              />
            )}

            {isEmployeeOrAdmin && activeTab === 'inventory' && (
              <TabInventory
                loadingInventory={loadingInventory}
                inventory={inventory}
                fetchInventory={fetchInventory}
                handleUpdateStock={handleUpdateStock}
                handleAssignTool={handleAssignTool}
                user={user}
                toast={toast}
              />
            )}

            {userRole === 'admin' && activeTab === 'users_admin' && (
              <TabTeamManagement adminUser={user} />
            )}

            {(userRole === 'admin' || userRole === 'employee') && activeTab === 'timesheet_admin' && (
              <TabTimesheetAdmin />
            )}

            {isEmployeeOrAdmin && activeTab === 'recruitment' && (
              <TabRecruitment user={user} />
            )}

            {(userRole === 'worker' || userRole === 'foreman' || userRole === 'admin' || userRole === 'employee') && activeTab === 'expenses' && (
              <TabExpenses
                user={user}
                pb={pb}
                isEmployeeOrAdmin={isEmployeeOrAdmin}
              />
            )}
          </main>
        </div>
      </div>

      {/* --- MOBILE DRAWER (Tiroir) --- */}
      <div className={`${styles.mobileDrawer} ${isMobileMenuOpen ? styles.drawerOpen : ''}`}>
        <div className={styles.drawerHeader}>
          <h2 className={styles.drawerTitle}>Menu</h2>
          <button onClick={() => setIsMobileMenuOpen(false)} className={styles.closeDrawerBtn}>
            <FaTimes />
          </button>
        </div>
        <nav className={styles.drawerNav}>
          <ul>
            {filteredMenu.map(item => (
              <li key={item.id}>
                <button
                  className={`${styles.navItem} ${activeTab === item.id ? styles.activeNav : ''}`}
                  onClick={() => {
                    setActiveTab(item.id);
                    setIsMobileMenuOpen(false);
                  }}
                  style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '12px 15px', color: 'var(--color-text)', fontSize: '0.95rem', fontWeight: 600, borderRadius: '8px' }}
                >
                  <span style={{ marginRight: '12px', fontSize: '1.2rem', color: activeTab === item.id ? 'var(--color-primary)' : 'var(--color-text-light)' }}>
                    {item.icon}
                  </span>
                  <span style={{ color: activeTab === item.id ? 'var(--color-primary)' : 'inherit' }}>
                    {item.label}
                  </span>
                  {item.showDot && item.count > 0 && (
                    <span style={{ marginLeft: 'auto', background: '#ef4444', color: '#fff', fontSize: '0.7rem', padding: '2px 6px', borderRadius: '10px' }}>
                      {item.count}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>
        <div className={styles.drawerFooter}>
          <button onClick={handleLogout} style={{ width: '100%', padding: '12px', background: 'none', border: '1px solid var(--color-light-gray)', borderRadius: '8px', color: 'var(--color-text)', fontWeight: 600, cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
            <FaSignOutAlt /> Déconnexion
          </button>
        </div>
      </div>

      {/* --- MOBILE OVERLAY --- */}
      {isMobileMenuOpen && <div className={styles.drawerOverlay} onClick={() => setIsMobileMenuOpen(false)}></div>}

      {/* --- MOBILE BOTTOM DOCK (Native App Style) --- */}
      <div className={styles.mobileBottomDock}>
        {filteredMenu.slice(0, 4).map(item => (
          <button 
            key={item.id} 
            onClick={() => setActiveTab(item.id)}
            className={activeTab === item.id ? styles.active : ''}
          >
            {item.icon}
            <span>{item.label === 'Vue d\'ensemble' ? 'Accueil' : item.label.replace('Mon ', '').replace('Mes ', '')}</span>
          </button>
        ))}
      </div>



      {/* MODAL DETAIL PROJET */}
      {
        showProjectModal && selectedProject && (
          <div className={styles.modalOverlay} onClick={() => setShowProjectModal(false)}>
            <div className={`${styles.modalCard} ${styles.modalLarge}`} onClick={e => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <h3>Journal de Bord : {selectedProject.name}</h3>
                <button className={styles.closeBtn} onClick={() => setShowProjectModal(false)}>×</button>
              </div>

              <div className={styles.projectDetailScroll}>
                <div className={styles.projectOverview}>
                  <div className={styles.overviewInfo}>
                    <p><strong>Adresse :</strong> {selectedProject.address}</p>
                    <p><strong>Statut :</strong> <span className={`${styles.statusBadge} ${styles['status' + selectedProject.status]}`}>{selectedProject.status}</span></p>
                  </div>
                  <div className={styles.progressBox}>
                    <div className={styles.progressLabel}>
                      <span>Avancement global</span>
                      <span>{selectedProject.progress || 0}%</span>
                    </div>
                    <div className={styles.progressBar}>
                      <div className={styles.progressFill} style={{ width: `${selectedProject.progress || 0}%` }}></div>
                    </div>
                  </div>
                </div>
                <div className={styles.timelineSection}>
                  <h4><FaProjectDiagram /> Étapes du Projet</h4>
                  <div className={styles.timeline}>
                    {[
                      { label: "Étude & Devis", status: "completed", date: "Jan 2026" },
                      { label: "Validation Technique", status: "completed", date: "Fév 2026" },
                      { label: "Commande Matériaux", status: selectedProject.progress > 20 ? "completed" : "current", date: "En cours" },
                      { label: "Installation Chantier", status: selectedProject.progress > 40 ? "completed" : "pending" },
                      { label: "Pose & Assemblage", status: selectedProject.progress > 70 ? "completed" : "pending" },
                      { label: "Finitions & Livraison", status: selectedProject.progress === 100 ? "completed" : "pending" },
                    ].map((step, i) => (
                      <div key={i} className={`${styles.timelineStep} ${styles[step.status]}`}>
                        <div className={styles.stepIcon}>
                          {step.status === 'completed' ? <FaCheckCircle /> : <div className={styles.stepDot}></div>}
                        </div>
                        <div className={styles.stepInfo}>
                          <span className={styles.stepLabel}>{step.label}</span>
                          {step.date && <span className={styles.stepDate}>{step.date}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                  {selectedProject.progress === 100 && (
                    <div className={styles.surveyWidget}>
                      <h4>Votre avis compte !</h4>
                      <p>Comment avez-vous trouvé notre intervention ?</p>
                      <div className={styles.surveyStars}>
                        {[1, 2, 3, 4, 5].map(star => (
                          <FaStar key={star} className={styles.surveyStar} />
                        ))}
                      </div>
                      <button className={styles.surveyBtn}>Envoyer</button>
                    </div>
                  )}
                </div>
                <div className={styles.beforeAfterSection}>
                  <h4><FaChevronRight /> Evolution : Avant / Après</h4>
                  <div className={styles.sliderContainer}>
                    <div className={styles.beforeImage}>
                      <img src="/images/projet-prev-1.jpg" alt="Avant" />
                    </div>
                    <div
                      className={styles.afterImage}
                      style={{ clipPath: `inset(0 0 0 ${sliderValue}%)` }}
                    >
                      <img src="/images/projet-detail-1.jpg" alt="Après" />
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={sliderValue}
                      onChange={(e) => setSliderValue(e.target.value)}
                      className={styles.comparisonSlider}
                    />
                    <div className={styles.sliderLabels}>
                      <span>Etat Initial</span>
                      <span>Projet Actuel</span>
                    </div>
                  </div>
                </div>

                <div className={styles.gallerySection}>
                  <h4><FaImage /> Photos du Chantier</h4>
                  {selectedProject.photos && selectedProject.photos.length > 0 ? (
                    <div className={styles.photoGrid}>
                      {selectedProject.photos.map((photo, index) => (
                        <div key={index} className={styles.photoCard}>
                          <img
                            src={pb.files.getURL(selectedProject, photo)}
                            alt={`Chantier ${index}`}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.emptyGallery}>
                      <p>Votre artisan n'a pas encore ajouté de photos pour le moment.</p>
                    </div>
                  )}
                </div>

                <div className={styles.technicalNote}>
                  <h4>Notes Techniques</h4>
                  <p>{selectedProject.description || "Aucune note particulière pour le moment."}</p>
                </div>
              </div>
            </div>
          </div>
        )
      }



      {/* MODAL UPLOAD PHOTO / RAPPORT ASSISTÉ */}
      {
        showUploadModal && selectedProject && (
          <div className={styles.modalOverlay} onClick={() => {
            setShowUploadModal(false);
            setVoiceNote('');
          }}>
            <div className={`${styles.modalCard} ${styles.modalLarge}`} onClick={e => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <FaImage style={{ color: 'var(--color-primary)' }} />
                  <h3 style={{ margin: 0 }}>Rapport de Chantier Intelligent</h3>
                </div>
                <button className={styles.closeBtn} onClick={() => {
                  setShowUploadModal(false);
                  setVoiceNote('');
                }}>×</button>
              </div>

              <form onSubmit={handlePhotoUpload} className={styles.photoReportLayout} style={{ padding: '25px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>

                  {/* Visual Section */}
                  <div>
                    <label className={styles.kpiLabel} style={{ marginBottom: '10px', display: 'block' }}>Aperçu du Rapport (Watermark Auto)</label>
                    <div className={styles.watermarkPreview}>
                      <FaImage size={40} style={{ opacity: 0.2 }} />
                      <div className={styles.watermarkOverlay}>
                        <div style={{ fontWeight: '800', color: 'var(--color-primary)' }}>MAISON QUINARD - EXEC</div>
                        <div>PROJET: {selectedProject.name.toUpperCase()}</div>
                        <div>DATE: {new Date().toLocaleDateString('fr-FR')}</div>
                        <div>PHASE: {reportPhase.replace('_', ' ')}</div>
                        {isCriticalPoint && <div style={{ color: '#ef4444', fontWeight: '800' }}>⚠️ POINT CRITIQUE</div>}
                      </div>
                    </div>

                    <div className={styles.fileUploadArea} style={{ marginTop: '20px', height: '100px' }}>
                      <input type="file" multiple className={styles.hiddenInput} />
                      <FaPlus /> <span>Ajouter des clichés haute résolution</span>
                    </div>
                  </div>

                  {/* Data Section */}
                  <div>
                    <div className={styles.formGroup}>
                      <label>Phase de construction</label>
                      <div className={styles.phaseGrid}>
                        {['OSSATURE', 'CHARPENTE', 'POUTRES_MURS', 'ETANCHEITE', 'FINITIONS', 'RECEPTION'].map(p => (
                          <div
                            key={p}
                            className={`${styles.phaseChip} ${reportPhase === p ? styles.active : ''}`}
                            onClick={() => setReportPhase(p)}
                          >
                            {p.replace('_', ' ')}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className={styles.voiceTerminal}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <label style={{ fontWeight: '800', fontSize: '0.9rem' }}>Observations (Dictée Vocale)</label>
                        <button
                          type="button"
                          onClick={startVoiceInput}
                          className={`${styles.micPulseBtn} ${isListening ? styles.active : ''}`}
                        >
                          <FaMicrophone />
                        </button>
                      </div>
                      <textarea
                        rows="4"
                        placeholder="Appuyez sur le micro pour dicter vos notes techniques..."
                        className={styles.modalSelect}
                        style={{ background: 'white', height: '100px', padding: '12px' }}
                        value={voiceNote}
                        onChange={(e) => setVoiceNote(e.target.value)}
                      />
                    </div>

                    <div style={{ marginTop: '20px' }}>
                      <div className={styles.technicalSwitch}>
                        <input
                          type="checkbox"
                          id="critical"
                          checked={isCriticalPoint}
                          onChange={(e) => setIsCriticalPoint(e.target.checked)}
                          style={{ width: '18px', height: '18px' }}
                        />
                        <label htmlFor="critical" style={{ cursor: 'pointer' }}>Marquer comme Point Critique (Contrôle Qualité)</label>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '15px', marginTop: '30px', borderTop: '1px solid #e2e8f0', paddingTop: '20px' }}>
                  <button
                    type="button"
                    className={styles.exportBtn}
                    style={{ flex: 1 }}
                    onClick={() => setShowUploadModal(false)}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className={styles.submitButton}
                    style={{ flex: 2, background: 'var(--color-primary)', color: 'white' }}
                  >
                    <FaPaperPlane /> Publier le Rapport & Notifier le Client
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* MODAL DECLARATION HEURES */}
      {
        showTimesheetModal && (
          <div className={styles.modalOverlay} onClick={() => setShowTimesheetModal(false)}>
            <div className={styles.modalCard} onClick={e => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <h3>Déclarer mes Heures</h3>
                {!navigator.onLine && <span style={{ color: '#f59e0b', fontSize: '0.8rem', marginLeft: 'auto', marginRight: '15px' }}>OFFLINE</span>}
                <button className={styles.closeBtn} onClick={() => setShowTimesheetModal(false)}>×</button>
              </div>

              {/* Geolocation Status */}
              <div style={{ padding: '0 20px', marginBottom: '10px', fontSize: '0.85rem' }}>
                {locationStatus === 'locating' && <span style={{ color: '#3b82f6', display: 'flex', alignItems: 'center', gap: '6px' }}><FaLocationArrow /> Géolocalisation...</span>}
                {locationStatus === 'success' && <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '6px' }}><FaMapMarkerAlt /> Position validée</span>}
                {locationStatus === 'error' && <span style={{ color: '#ef4444', display: 'flex', alignItems: 'center', gap: '6px' }}><FaExclamationTriangle /> Position non détectée (Continuons)</span>}
              </div>

              {todaySummary && (
                <div style={{ padding: '0 20px', marginBottom: '20px' }}>
                  <div className={styles.sessionRecap}>
                    <div className={styles.recapTitle}>
                      <FaHistory /> RÉCAPITULATIF LIVE DU JOUR
                    </div>
                    <div className={styles.recapList}>
                      {todaySummary.details.map((item, idx) => (
                        <div key={idx} className={styles.recapItem}>
                          <span className={styles.recapMission}>{item.name}</span>
                          <span className={styles.recapHours}>{Math.round(item.hours * 10) / 10}h</span>
                        </div>
                      ))}
                      <div className={styles.recapTotal}>
                        <span>TOTAL DÉTECTÉ</span>
                        <span style={{ color: 'var(--color-primary)' }}>{todaySummary.total}h</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleTimesheetSubmit} className={styles.modalForm}>
                <div className={styles.formGroup}>
                  <label>Chantier</label>
                  <select
                    className={styles.modalSelect}
                    value={newTimesheet.chantier}
                    onChange={e => setNewTimesheet({ ...newTimesheet, chantier: e.target.value })}
                    required
                  >
                    <option value="">Sélectionnez un chantier</option>
                    {staffProjects.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                    <option value="general">Général / Atelier</option>
                  </select>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label>Date</label>
                    <input
                      type="date"
                      value={newTimesheet.date}
                      onChange={e => setNewTimesheet({ ...newTimesheet, date: e.target.value })}
                      required
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>Nb Heures Total</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      placeholder="7.5"
                      style={{ background: 'rgba(164,198,57,0.05)', fontWeight: 900 }}
                      value={newTimesheet.hours}
                      onChange={e => setNewTimesheet({ ...newTimesheet, hours: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className={styles.timeDecomposition}>
                  <div className={styles.timeBlock}>
                    <label>MATIN</label>
                    <div className={styles.timeInputs}>
                      <input type="time" value={newTimesheet.morning_start} onChange={e => setNewTimesheet({ ...newTimesheet, morning_start: e.target.value })} />
                      <span>à</span>
                      <input type="time" value={newTimesheet.morning_end} onChange={e => setNewTimesheet({ ...newTimesheet, morning_end: e.target.value })} />
                    </div>
                  </div>
                  <div className={styles.timeBlock}>
                    <label>APRÈS-MIDI</label>
                    <div className={styles.timeInputs}>
                      <input type="time" value={newTimesheet.afternoon_start} onChange={e => setNewTimesheet({ ...newTimesheet, afternoon_start: e.target.value })} />
                      <span>à</span>
                      <input type="time" value={newTimesheet.afternoon_end} onChange={e => setNewTimesheet({ ...newTimesheet, afternoon_end: e.target.value })} />
                    </div>
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label>Description du travail (Optionnel)</label>
                  <textarea
                    rows="3"
                    placeholder="Pose bardage, ossature..."
                    value={newTimesheet.description}
                    onChange={e => setNewTimesheet({ ...newTimesheet, description: e.target.value })}
                  ></textarea>
                </div>

                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button
                    type="button"
                    className={styles.submitButton}
                    style={{ flex: 1, background: 'var(--color-bg-secondary)', color: 'var(--color-text-primary)' }}
                    onClick={() => setShowTimesheetModal(false)}
                  >
                    Annuler
                  </button>
                  <button type="submit" className={styles.submitButton} style={{ flex: 2 }}>
                    {navigator.onLine ? 'Valider la saisie' : 'Enregistrer localement'}
                  </button>
                </div>
              </form>
            </div >
          </div >
        )
      }

      {/* ── SCROLL TO TOP BUTTON ── */}
      {
        showScrollTop && (
          <button
            className={styles.scrollToTopBtn}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            title="Retour en haut"
            aria-label="Retour en haut de la page"
          >
            &#8679;
          </button>
        )
      }
    </div >
  );
}

export default AccountPage;
